import {useQuery} from '@tanstack/react-query'
import {useLocation} from 'react-router-dom'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {usePreviewContext} from '../contexts/preview-context'

/**
 * Deep merge preview content into fetched data
 * Finds blocks by ContentLink.GuidValue and replaces with preview version
 * When the preview IS the page itself (ContainerPage), replace the entire page
 */
const mergePreviewContent = (data, previewContent) => {
    if (!data || !previewContent?.items?.[0]) {
        return data
    }

    const previewItem = previewContent.items[0]
    const previewKey = previewItem?.ContentLink?.GuidValue

    if (!previewKey) {
        return data
    }

    // Check if the preview content IS the page itself (ContainerPage)
    // In this case, items[0] of both data and preview have the same GuidValue
    if (data?.items?.[0]?.ContentLink?.GuidValue === previewKey) {
        // Replace the entire page with preview content, preserving the {items: [...]} structure
        return {
            ...data,
            items: [previewItem]
        }
    }

    // Helper to recursively find and replace content by GuidValue
    const replaceContent = (obj) => {
        if (!obj || typeof obj !== 'object') return obj

        // Check if this object has a matching ContentLink.GuidValue
        if (obj?.ContentLink?.GuidValue === previewKey) {
            // Replace the Expanded content with preview content
            return {
                ...obj,
                ContentLink: {
                    ...obj.ContentLink,
                    Expanded: previewItem
                }
            }
        }

        // Recursively process arrays
        if (Array.isArray(obj)) {
            return obj.map((item) => replaceContent(item))
        }

        // Recursively process object properties
        const result = {}
        for (const key of Object.keys(obj)) {
            result[key] = replaceContent(obj[key])
        }
        return result
    }

    return replaceContent(data)
}

export function useOptimizelyPage(customPath) {
    const location = useLocation()
    // Use custom path if provided (e.g., 'header', 'homepage', 'pdp')
    // If customPath is explicitly null, don't fall back to pathname (query will be disabled)
    // If customPath is undefined, fall back to current route pathname
    const address = customPath === null ? null : (customPath || location.pathname)
    const appOrigin = useAppOrigin()

    // During SSR, use PWA_DOMAIN_SITE from env (bypasses basic auth issues on MRT)
    const isSSR = typeof window === 'undefined'
    let fetchOrigin = appOrigin
    if (isSSR) {
        const pwaDomain = process.env.PWA_DOMAIN_SITE
        if (pwaDomain) {
            const protocol = pwaDomain.startsWith('localhost') ? 'http' : 'https'
            fetchOrigin = `${protocol}://${pwaDomain}`
        }
    }

    // Check if we're in preview mode and this matches the preview page identifier
    const {previewContent, pageIdentifier, isPreviewMode} = usePreviewContext()
    const shouldMergePreview = isPreviewMode && pageIdentifier === customPath

    const query = useQuery({
        queryKey: ['optimizely-page', address],
        queryFn: async () => {
            const res = await fetch(
                `${fetchOrigin}/optimizely-page?path=${encodeURIComponent(address)}`
            )
            const json = await res.json()

            // Handle both old single query format and new combined query format
            if (json?.data?.page) {
                // New combined query format
                return json.data.page
            } else if (json?.data?.Content) {
                // Old single query format
                return json.data
            } else {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('⚠️ Unknown Optimizely data format')
                }
                return json?.data
            }
        },
        // Keep data fresh for 24 hours - CMS content rarely changes
        staleTime: 24 * 60 * 60 * 1000,
        // Keep in cache for 24 hours
        gcTime: 24 * 60 * 60 * 1000,
        // Only fetch when we have a valid address (prevents fetching with null customPath)
        enabled: !!address,
        // Disable automatic refetching for static CMS content
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false
    })

    // If in preview mode and this matches the preview identifier, merge preview content
    if (shouldMergePreview && query.data) {
        const mergedData = mergePreviewContent(query.data, previewContent)
        return {
            ...query,
            data: mergedData
        }
    }

    return query
}
